import { defineView } from "eddev/views"
import { Col, Container, Grid } from "@components/layout/Grid"
import { PageHeading } from "@components/forms/atoms/PageHeading"
import { ComponentPropsWithoutRef, forwardRef, useEffect, useMemo, useRef, useState } from "react"
import { cn } from "@utils/tw"
import { Button } from "@components/atoms/Button"
import { useCategories, useDocumentArchive, useDocumentTotals } from "@hooks/queries"
import { OrderEnum, PostObjectsConnectionOrderbyEnum } from "../types.graphql"
import { Loader, SelectedFiltersChips, Showing } from "@components/atoms/archive/Components"
import { Pagination } from "@components/molecules/archive/Pagination"
import { SelectedFiltersT } from "@components/molecules/archive/types"
import { Filter } from "@components/molecules/archive/Filter"
import { ButtonSelections } from "@components/molecules/archive/ButtonSelections"
import { useIsMobile } from "@hooks/useViewportDerived"
import { AnimateCard } from "@components/molecules/archive/AnimateCard"
import { AnimateRow } from "@components/molecules/archive/AnimateRow"
import { Breadcrumbs } from "@components/atoms/Breadcrumbs"
import { useHydrating, useRoute } from "eddev/routing"
import { CLOSE } from "@components/atoms/Icons"
import { twMerge } from "tailwind-merge"

export const meta: ViewMeta = {
  templateName: "Archive: Library",
}

const postsPerPage = 48

export default defineView("archive-library", (props) => {
  const searchForm = useRef<HTMLFormElement>(null)
  const taxonomies = useCategories(undefined)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [keywordSearch, setKeywordSearch] = useState<string>("")
  const [inputText, setInputText] = useState<string>("")
  const [selectedFilters, setSelectedFilters] = useState<SelectedFiltersT>({
    documentTypes: [],
    documentTopics: [],
    sort: {
      name: "Latest first",
      uri: "latest-first",
      slug: "latest-first",
    },
  })
  const [listView, setListView] = useState<boolean>(true)
  const isMobile = useIsMobile()

  const totals = useDocumentTotals({
    documentTypeSlug:
      selectedFilters.documentTypes!.length > 0
        ? selectedFilters.documentTypes!.map((type) => type.slug)
        : props.documentTypes?.nodes.map((node) => node.slug),
    documentTopicSlug:
      selectedFilters.documentTopics!.length > 0
        ? selectedFilters.documentTopics!.map((type) => type.slug)
        : props.documentTopics?.nodes.map((node) => node.slug),
    search: keywordSearch,
  }).data?.documents?.edges

  const pages = useMemo(() => {
    const cursors = []

    if (totals) {
      for (let i = 0; i < totals.length; i += postsPerPage) {
        if (totals[i] && totals[i] && totals[i].cursor) {
          cursors.push(totals[i].cursor)
        }
      }
    }

    return cursors
  }, [totals])

  useEffect(() => {
    setCurrentPage(1)
  }, [selectedFilters])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [currentPage])

  const getKeywordSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    // @ts-ignore
    setKeywordSearch((e.target[0].value as string) ?? "")
  }

  const submitOnEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault()
      setKeywordSearch(e.currentTarget.value)
    }
  }

  useEffect(() => {
    setInputText(keywordSearch)
  }, [keywordSearch])

  const route = useRoute()
  const hydrating = useHydrating()
  const [showClear, setShowClear] = useState<boolean>(false)

  return (
    <>
      {route.pathname && <Breadcrumbs uri={route.pathname} page={props.page} />}
      {!hydrating && taxonomies.data && (
        <div className="grid-auto gap-y-4 lg:gap-y-6">
          <div className="col-span-12 md:col-span-10 md:col-start-2 lg:col-span-8 lg:col-start-3 xl:col-span-6 xl:col-start-4">
            <PageHeading title={props.page?.title ?? "News Archive"} />
          </div>
          <div className="col-span-12">
            <form
              className="flex flex-col gap-3 mb-6 sm:w-full sm:mb-0 sm:flex-row sm:[&_input]:flex-grow"
              onSubmit={(e) => getKeywordSearch(e)}
              ref={searchForm}
            >
              <div className="relative flex-grow">
                <input
                  className={twMerge(
                    "type-label-l w-full h-full px-3 py-4 rounded-[6px] border-none",
                    "ring-1 ring-inset ring-mid-grey focus:outline-none focus:ring-2 focus:ring-black",
                  )}
                  placeholder="Search articles"
                  value={inputText}
                  onChange={(e) => {
                    setInputText(e.target.value)
                    if (e.target.value !== "") setShowClear(true)
                  }}
                  onKeyDown={(e) => submitOnEnter(e)}
                />
                {showClear && (
                  <button
                    className="absolute z-[1] top-1/2 right-4 -translate-y-1/2"
                    onClick={(e) => {
                      e.preventDefault()
                      setShowClear(false)
                      setKeywordSearch("")
                    }}
                  >
                    {CLOSE}
                  </button>
                )}
              </div>
              <Button size="large" color="yellow" icon="SEARCH" iconStyle="right">
                Search
              </Button>
            </form>
          </div>

          <div className="col-span-8">
            <Filter
              documentTypes={props.documentTypes?.nodes ?? []}
              documentTopics={props.documentTopics?.nodes ?? []}
              setSelectedFilters={setSelectedFilters}
              selectedFilters={selectedFilters}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              keywordSearch={keywordSearch}
              setKeywordSearch={setKeywordSearch}
              taxonomies={taxonomies.data}
            />
          </div>

          <div className="col-span-4">
            <div className="w-full flex items-center justify-end gap-x-3 [&_button_span]:hidden">
              <Button
                size={isMobile ? "large" : "tiny"}
                color={listView ? "white" : "grey"}
                icon="GRID"
                iconStyle="left"
                onClick={() => setListView(false)}
              />
              <Button
                size={isMobile ? "large" : "tiny"}
                color={listView ? "grey" : "white"}
                icon="LIST"
                iconStyle="left"
                onClick={() => setListView(true)}
              />
            </div>
          </div>

          {pages && totals ? (
            <>
              <CurrentPosts
                page={currentPage}
                documentTypes={
                  selectedFilters.documentTypes!.length > 0
                    ? selectedFilters.documentTypes!.map((type) => type.slug)
                    : props.documentTypes?.nodes.map((node) => node.slug)
                }
                documentTopics={
                  selectedFilters.documentTopics!.length > 0
                    ? selectedFilters.documentTopics!.map((type) => type.slug)
                    : props.documentTopics?.nodes.map((node) => node.slug)
                }
                keyword={keywordSearch}
                cursor={currentPage === 1 ? "" : pages[currentPage - 1] ?? ""}
                total={totals.length ?? 0}
                selectedFilters={selectedFilters}
                setSelectedFilters={setSelectedFilters}
                listView={listView}
              />

              <div className="col-span-12" />

              {pages.length > 1 && (
                <div className="col-span-12 md:col-span-6 md:col-start-4 lg:col-span-4 lg:col-start-5">
                  <Pagination pages={pages} currentPage={currentPage} setCurrentPage={setCurrentPage} />
                </div>
              )}
            </>
          ) : (
            <Loader listView={listView} />
          )}
        </div>
      )}
    </>
  )
})

const CurrentPosts = ({
  page,
  keyword,
  documentTypes,
  documentTopics,
  cursor,
  total,
  selectedFilters,
  setSelectedFilters,
  listView,
}: {
  page: number
  keyword: string
  documentTypes?: (string | null)[]
  documentTopics?: (string | null)[]
  cursor: string
  total: number
  selectedFilters: SelectedFiltersT
  setSelectedFilters: React.Dispatch<React.SetStateAction<SelectedFiltersT>>
  listView: boolean
}) => {
  const posts = useDocumentArchive({
    documentTypeSlug: documentTypes,
    documentTopicSlug: documentTopics,
    search: keyword,
    cursor: cursor,
    field:
      selectedFilters.sort.slug === "latest-first"
        ? PostObjectsConnectionOrderbyEnum.Date
        : selectedFilters.sort.slug === "oldest-first"
          ? PostObjectsConnectionOrderbyEnum.Date
          : PostObjectsConnectionOrderbyEnum.Title,
    order:
      selectedFilters.sort.slug === "latest-first"
        ? OrderEnum.Desc
        : selectedFilters.sort.slug === "oldest-first"
          ? OrderEnum.Asc
          : OrderEnum.Asc,
  })

  if (posts.data?.documents?.nodes)
    return (
      <>
        <div className="col-span-12 md:col-span-6 lg:col-span-4">
          <Showing>
            Showing {page * postsPerPage - (postsPerPage - 1)} to{" "}
            {page * postsPerPage < total ? page * postsPerPage : total} of {total} articles
          </Showing>
        </div>
        <div className="col-span-12 md:col-span-6 lg:col-span-8">
          <SelectedFiltersChips>
            {selectedFilters?.documentTypes?.map((tax, i) => (
              <ButtonSelections
                taxonomy={tax}
                type="documentTypes"
                style="chip"
                selectedFilters={selectedFilters}
                setSelectedFilters={setSelectedFilters}
                key={i}
              />
            ))}
            {selectedFilters?.documentTopics?.map((cat, i) => (
              <ButtonSelections
                taxonomy={cat}
                type="documentTopics"
                style="chip"
                selectedFilters={selectedFilters}
                setSelectedFilters={setSelectedFilters}
                key={i}
              />
            ))}
          </SelectedFiltersChips>
        </div>
        {listView ? (
          <div className="col-span-12 min-h-[400px]">
            <ul>
              {posts.data.documents.nodes.map((document, i) => (
                <li key={i}>
                  <AnimateRow article={document} order={i} />
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <>
            {posts.data.documents.nodes.map((document, i) => (
              <div className="col-span-12 sm:col-span-6 lg:col-span-4" key={i}>
                <AnimateCard article={document} order={i} type="document" />
              </div>
            ))}
          </>
        )}
      </>
    )

  return <Loader listView={listView} />
}
